/**
 * Compare Table v2.0
 * Manages the compare table block frontend
 * Uses CompareState for data and CompareAddProductModal for add product functionality
 *
 * v2.0 Features:
 * - Dynamic attribute rows (all product attributes)
 * - Highlight differences toggle
 * - Category badge display
 * - 4-column fixed layout with empty slot placeholders
 * - Alphabetically sorted attributes
 */
const CompareTable = {
  /**
   * Highlight differences mode state
   */
  highlightEnabled: false,

  /**
   * Maximum number of columns to display (always 4)
   */
  maxColumns: 4,

  /**
   * Fixed rows that always appear (in order)
   */
  fixedRows: ["main", "rating"],

  /**
   * Fixed row labels
   */
  fixedRowLabels: {
    main: "",
    rating: "Rating",
  },

  /**
   * Initialize compare table functionality
   */
  init: function () {
    // Always subscribe to state changes (handles dynamically created blocks too)
    this._subscribeToStateChanges();
    this._bindAddToCartEvents();

    const blocks = document.querySelectorAll(".wp-block-brandy-compare-table");
    if (blocks.length === 0) return;

    this._initPopupFunctionality();
    this._loadInitialData(Array.from(blocks));
  },

  /**
   * Subscribe to CompareState changes
   * @private
   */
  _subscribeToStateChanges: function () {
    if (!window.CompareState) return;

    const self = this;
    window.CompareState.on("change", function (data) {
      const blocks = document.querySelectorAll(
        ".wp-block-brandy-compare-table"
      );
      if (blocks.length > 0) {
        self._renderBlocks(Array.from(blocks), data.products);
      }
    });

    // Listen for categories-changed to update category badge
    window.CompareState.on("categories-changed", function (data) {
      self._updateCategoryBadges(data.sharedCategories);
    });
  },

  /**
   * Load initial data for blocks
   * @private
   * @param {Array<HTMLElement>} blocks - Compare table blocks
   */
  _loadInitialData: function (blocks) {
    const self = this;

    if (window.CompareState) {
      window.CompareState.fetchProducts().then(function (products) {
        self._renderBlocks(blocks, products);
      });
    }
  },

  /**
   * Render all compare table blocks
   * @private
   * @param {Array<HTMLElement>} blocks - Compare table blocks
   * @param {Array} products - Products data
   */
  _renderBlocks: function (blocks, products) {
    const self = this;

    blocks.forEach(function (block) {
      if (products.length === 0) {
        self._renderEmptyState(block);
      } else {
        self._renderTable(block, products);
      }
    });

    // Re-init popup and touch support after render
    setTimeout(function () {
      self._initPopupFunctionality();
      self._addTouchSupport();
    }, 100);
  },

  /**
   * Render empty state for block
   * @private
   * @param {HTMLElement} block - Block element
   */
  _renderEmptyState: function (block) {
    const wrapper = block.querySelector(".compare-table-wrapper");
    const contentWrapper = block.querySelector(
      ".compare-table-content-wrapper"
    );
    const skeleton = block.querySelector(
      ".brandy-compare-table.skeleton-table"
    );

    if (skeleton) skeleton.style.display = "none";

    // Remove existing placeholder
    const existing = block.querySelector(".compare-table-placeholder");
    if (existing) existing.remove();

    // Remove existing toolbar
    const existingToolbar = block.querySelector(".compare-table-toolbar");
    if (existingToolbar) existingToolbar.remove();

    // Add placeholder
    const placeholder = document.createElement("div");
    placeholder.className = "compare-table-placeholder";
    placeholder.innerHTML = `
      <p>No products in compare list</p>
      <button class="add-product-button" type="button">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
        Add Product
      </button>
    `;

    // For popup mode, append inside the wrapper so it shows in the popup
    // For inline mode, append to block directly
    const isPopup = wrapper && wrapper.classList.contains("is-popup");
    if (isPopup) {
      // Hide the content wrapper but show the inner compare table for popup
      if (contentWrapper) contentWrapper.style.display = "none";

      // Find the brandy-compare-table inside wrapper (not content wrapper)
      let popupContent = wrapper.querySelector(".brandy-compare-table");
      if (popupContent) {
        // Clear and add placeholder
        popupContent.innerHTML = "";
        popupContent.appendChild(placeholder);
        // Ensure it's visible
        popupContent.style.display = "block";
      } else {
        // Create container if not exists
        popupContent = document.createElement("div");
        popupContent.className = "brandy-compare-table";
        popupContent.appendChild(placeholder);
        wrapper.appendChild(popupContent);
      }
    } else {
      // Inline mode
      if (contentWrapper) contentWrapper.style.display = "none";
      block.appendChild(placeholder);
    }

    // Bind add button
    const btn = placeholder.querySelector(".add-product-button");
    if (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        if (window.CompareAddProductModal) window.CompareAddProductModal.show();
      });
    }

    block.classList.remove("has-products");
  },

  /**
   * Render table with products
   * @private
   * @param {HTMLElement} block - Block element
   * @param {Array} products - Products data
   */
  _renderTable: function (block, products) {
    const wrapper = block.querySelector(".compare-table-wrapper");
    const contentWrapper = block.querySelector(
      ".compare-table-content-wrapper"
    );
    const skeleton = block.querySelector(".skeleton-table");

    if (!wrapper || !contentWrapper) return;

    // Get table from content wrapper (not skeleton)
    const table = contentWrapper.querySelector(".compare-table");
    if (!table) return;

    // Generate and set table HTML
    table.innerHTML = this._generateTableHTML(products);
    this._addStickyFirstColumn(table);

    // Add toolbar with highlight toggle and category badge
    this._ensureToolbar(block, contentWrapper, products);

    // Remove any existing external add product button (no longer needed)
    const existingBtn = block.querySelector(".add-product-button-external");
    if (existingBtn) existingBtn.remove();

    // Show table, hide skeleton and placeholder
    if (skeleton) skeleton.style.display = "none";
    contentWrapper.style.display = "block";

    const placeholder = block.querySelector(".compare-table-placeholder");
    if (placeholder) placeholder.remove();

    block.classList.add("is-loaded", "has-products");
  },

  /**
   * Generate table HTML from products data
   * @private
   * @param {Array} products - Products data
   * @returns {string} Table HTML
   */
  _generateTableHTML: function (products) {
    const self = this;
    let html = "<tbody>";

    // Pad products array to always have maxColumns slots
    const paddedProducts = this._padProductsToMax(products);

    // Fixed rows first
    this.fixedRows.forEach(function (field) {
      html += self._renderFixedRow(field, paddedProducts);
    });

    // Dynamic attribute rows (alphabetically sorted)
    const allAttributes = this._collectAllAttributes(products);
    allAttributes.sort().forEach(function (attr) {
      html += self._renderAttributeRow(attr, paddedProducts);
    });

    html += "</tbody>";
    return html;
  },

  /**
   * Pad products array to always have maxColumns slots
   * @private
   * @param {Array} products - Products data
   * @returns {Array} Padded products array with null for empty slots
   */
  _padProductsToMax: function (products) {
    const padded = [...products];
    while (padded.length < this.maxColumns) {
      padded.push(null); // null indicates empty slot
    }
    return padded;
  },

  /**
   * Collect all unique attributes across products
   * @private
   * @param {Array} products - Products data
   * @returns {Array} Unique attribute names
   */
  _collectAllAttributes: function (products) {
    const attrs = new Set();
    products.forEach(function (p) {
      if (p.attributes) {
        Object.keys(p.attributes).forEach(function (a) {
          attrs.add(a);
        });
      }
    });
    return Array.from(attrs);
  },

  /**
   * Render a fixed row (image, name, rating, price, add_to_cart)
   * @private
   * @param {string} field - Field name
   * @param {Array} products - Products data (may include null for empty slots)
   * @returns {string} Row HTML
   */
  _renderFixedRow: function (field, products) {
    const self = this;
    const label = this.fixedRowLabels[field];

    let html = `<tr class="compare-row compare-row-${field}"><td class="compare-field-label"><strong>${label}</strong></td>`;

    products.forEach(function (product) {
      const cellClass =
        field === "name"
          ? "compare-product-cell compare-product-header"
          : "compare-product-cell";

      if (product === null) {
        // Empty slot
        html += `<td class="${cellClass} compare-empty-slot">`;
        html += self._renderEmptySlotCell(field);
        html += "</td>";
      } else {
        html += `<td class="${cellClass}">`;
        html += self._renderFieldCell(field, product);
        html += "</td>";
      }
    });

    html += "</tr>";
    return html;
  },

  /**
   * Render empty slot cell content
   * @private
   * @param {string} field - Field name
   * @returns {string} Cell HTML for empty slot
   */
  _renderEmptySlotCell: function (field) {
    if (field === "main") {
      // Show Add Product button for main row empty slots
      return `
        <div class="compare-empty-slot-content compare-product-image">
          <button class="add-product-slot-button" type="button" onclick="if(window.CompareAddProductModal) window.CompareAddProductModal.show();">
            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.1504 0C15.7343 0.000242754 20.2997 4.56648 20.2998 10.1504C20.2996 15.7342 15.7342 20.2996 10.1504 20.2998C4.56648 20.2997 0.00024275 15.7343 0 10.1504C0.000143706 4.56641 4.56641 0.000143222 10.1504 0ZM10.1504 1.5C5.39484 1.50014 1.50014 5.39484 1.5 10.1504C1.50024 14.9059 5.3949 18.7997 10.1504 18.7998C14.9058 18.7996 18.7996 14.9058 18.7998 10.1504C18.7997 5.3949 14.9059 1.50024 10.1504 1.5ZM10.1445 5.63965C10.5587 5.63965 10.8945 5.97543 10.8945 6.38965V9.40332H13.9092C14.3233 9.40342 14.6592 9.73917 14.6592 10.1533C14.6589 10.5672 14.3231 10.9032 13.9092 10.9033H10.8945V13.9092C10.8944 14.3233 10.5587 14.6592 10.1445 14.6592C9.7306 14.6589 9.39463 14.3231 9.39453 13.9092V10.9033H6.38965C5.9756 10.9033 5.63991 10.5673 5.63965 10.1533C5.63965 9.73911 5.97543 9.40332 6.38965 9.40332H9.39453V6.38965C9.39453 5.9756 9.73054 5.63991 10.1445 5.63965Z" fill="#5A6D80"/>
            </svg>
            <span>Add Product</span>
          </button>
        </div>
      `;
    }

    // Show skeleton for other rows
    return `
      <div class="compare-empty-slot-content compare-skeleton-slot">
        <div class="skeleton-line"></div>
      </div>
    `;
  },

  /**
   * Render cell content for a fixed field
   * @private
   * @param {string} field - Field name
   * @param {Object} product - Product data
   * @returns {string} Cell HTML
   */
  _renderFieldCell: function (field, product) {
    switch (field) {
      case "main":
        return `
          <div class="compare-product-image">${product.image || ""}
          <button class="compare-remove-button"
            onclick="CompareTable.removeProduct(${product.id})"
            title="Remove from comparison"
            aria-label="Remove from comparison">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
          </div>
          <p class="compare-product-name">${product.name || ""}</p>
          <span class="compare-product-price">${product.price || ""}</span>
          <div class="compare-add-to-cart-btn">${
            product.add_to_cart || ""
          }</div>
        `;

      case "rating":
        const rating = parseFloat(product.rating) || 0;
        return `
          <div class="compare-product-rating">⭐ ${rating.toFixed(1)}/5</div>`;

      default:
        return `<div class="compare-product-cell-default">${
          product[field] || ""
        }</div>`;
    }
  },

  /**
   * Render a dynamic attribute row
   * @private
   * @param {string} attrName - Attribute name
   * @param {Array} products - Products data (may include null for empty slots)
   * @returns {string} Row HTML
   */
  _renderAttributeRow: function (attrName, products) {
    // Only get values from non-null products for comparison
    const realProducts = products.filter(function (p) {
      return p !== null;
    });
    const realValues = realProducts.map(function (p) {
      return p.attributes?.[attrName] || "-";
    });
    const allSame = realValues.every(function (v) {
      return v === realValues[0];
    });
    const highlightClass =
      this.highlightEnabled && !allSame ? "highlight-difference" : "";

    let html = `<tr class="compare-row compare-row-attribute ${highlightClass}" data-attr="${attrName}">
      <td class="compare-field-label">
        <strong>${attrName}</strong>
      </td>`;

    products.forEach(function (p) {
      if (p === null) {
        // Empty slot - show skeleton
        html += `<td class="compare-product-cell compare-empty-slot">
          <div class="compare-empty-slot-content compare-skeleton-slot">
            <div class="skeleton-line"></div>
          </div>
        </td>`;
      } else {
        let value = p.attributes?.[attrName] || "-";
        const naClass = value === "-" ? "is-na" : "";
        if ( value.toLowerCase() === "yes" ) {
          value = '✔';
        }
        if ( value.toLowerCase() === "no" ) {
          value = '✘';
        }
        html += `<td class="compare-product-cell ${naClass}" data-value="${value}">${value}</td>`;
      }
    });

    html += "</tr>";
    return html;
  },

  /**
   * Ensure toolbar with highlight toggle, add product, and remove all buttons
   * @private
   * @param {HTMLElement} block - Block element
   * @param {HTMLElement} contentWrapper - Content wrapper
   * @param {Array} products - Products data
   */
  _ensureToolbar: function (block, contentWrapper, products) {
    const self = this;
    let toolbar = block.querySelector(".compare-table-toolbar");

    if (!toolbar) {
      toolbar = document.createElement("div");
      toolbar.className = "compare-table-toolbar";

      const tableContainer = contentWrapper.querySelector(
        ".compare-table-container"
      );
      if (tableContainer) {
        tableContainer.parentNode.insertBefore(toolbar, tableContainer);
      }
    }

    // Get shared categories
    const sharedCategories = window.CompareState
      ? window.CompareState.getSharedCategories()
      : [];

    // Check if at max limit for add button visibility
    const isAtMax = window.CompareState && window.CompareState.isAtMaxLimit();
    const hasProducts = products && products.length > 0;

    // Build toolbar HTML
    toolbar.innerHTML = `
      <div class="compare-toolbar-left">
        ${this._renderCategoryBadge(sharedCategories)}
      </div>
      <div class="compare-toolbar-right">
        <label class="highlight-toggle-wrapper">
          <input type="checkbox" class="highlight-toggle-checkbox" ${this.highlightEnabled ? "checked" : ""}>
          <span class="highlight-toggle-slider"></span>
          <span class="highlight-toggle-label">Highlight differences</span>
        </label>
        <button class="toolbar-btn add-product-btn" type="button" title="Add product" ${isAtMax ? "disabled" : ""}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.99902 0C13.95 0.000243746 17.9979 4.04797 17.998 8.99902C17.9978 13.95 13.95 17.9978 8.99902 17.998C4.04797 17.9979 0.000243751 13.95 0 8.99902C0.000128386 4.0479 4.0479 0.000128383 8.99902 0ZM8.99902 1.5C4.87633 1.50013 1.50013 4.87633 1.5 8.99902C1.50024 13.1216 4.8764 16.4979 8.99902 16.498C13.1216 16.4978 16.4978 13.1216 16.498 8.99902C16.4979 4.8764 13.1216 1.50024 8.99902 1.5ZM9.00195 4.94629C9.41591 4.94655 9.75189 5.28229 9.75195 5.69629V8.25195H12.3057C12.7198 8.25206 13.0557 8.58781 13.0557 9.00195C13.0554 9.41588 12.7196 9.75184 12.3057 9.75195H9.75195V12.2959C9.75184 12.7099 9.41588 13.0456 9.00195 13.0459C8.58781 13.0459 8.25206 12.71 8.25195 12.2959V9.75195H5.70605C5.29206 9.75189 4.95632 9.4159 4.95605 9.00195C4.95605 8.58778 5.2919 8.25202 5.70605 8.25195H8.25195V5.69629C8.25201 5.28213 8.58778 4.94629 9.00195 4.94629Z" fill="#5A6D80"/>
          </svg>
        </button>
        <button class="toolbar-btn remove-all-btn" type="button" title="Remove all" ${!hasProducts ? "disabled" : ""}>
          <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.78418 0C9.77171 2.43427e-05 10.6231 0.65083 10.9238 1.58203V1.58301L11.0586 2.00195L11.0811 2.06934C11.0983 2.06953 11.1168 2.07129 11.1367 2.07129C11.1433 2.07129 11.1498 2.0721 11.1562 2.07227H14.251C14.6652 2.07227 15.001 2.40805 15.001 2.82227C15.001 3.23648 14.6652 3.57227 14.251 3.57227H13.4443V11.6465C13.4443 12.405 13.4059 13.0636 13.2881 13.6182C13.1688 14.1795 12.958 14.6827 12.5771 15.0811C11.8244 15.8683 10.6415 16.002 9.24609 16.002H5.75684C4.36139 16.002 3.17857 15.8683 2.42578 15.0811C2.04488 14.6827 1.83414 14.1795 1.71484 13.6182C1.59701 13.0636 1.55859 12.405 1.55859 11.6465V3.57227H0.75C0.335786 3.57227 0 3.23648 0 2.82227C0 2.40805 0.335786 2.07227 0.75 2.07227H3.84766C3.85415 2.0721 3.86065 2.07129 3.86719 2.07129C3.88675 2.07129 3.90493 2.06952 3.92188 2.06934C3.92858 2.04873 3.93731 2.02677 3.94531 2.00195L4.08008 1.58301L4.14355 1.41211C4.48804 0.571386 5.29472 0.000152192 6.21973 0H8.78418ZM3.05859 11.6465C3.05859 12.3725 3.09669 12.9068 3.18164 13.3066C3.26518 13.6997 3.38365 13.913 3.50977 14.0449C3.77116 14.3182 4.31306 14.502 5.75684 14.502H9.24609C10.6901 14.502 11.2318 14.3182 11.4932 14.0449C11.6193 13.913 11.7378 13.6997 11.8213 13.3066C11.9062 12.9068 11.9443 12.3725 11.9443 11.6465V3.57227H3.05859V11.6465ZM5.94141 6.21484C6.35562 6.21484 6.69141 6.55063 6.69141 6.96484V11.1084C6.6913 11.5225 6.35555 11.8584 5.94141 11.8584C5.52726 11.8584 5.19151 11.5225 5.19141 11.1084V6.96484C5.19141 6.55063 5.52719 6.21484 5.94141 6.21484ZM9.57812 6.21484C9.99234 6.21484 10.3281 6.55063 10.3281 6.96484V11.1084C10.328 11.5225 9.99227 11.8584 9.57812 11.8584C9.16398 11.8584 8.82823 11.5225 8.82812 11.1084V6.96484C8.82812 6.55063 9.16391 6.21484 9.57812 6.21484ZM6.21973 1.5C5.91067 1.50016 5.61758 1.70401 5.50879 2.04199V2.04297L5.49902 2.07227H9.50586L9.49609 2.04297C9.38652 1.70379 9.09252 1.50002 8.78418 1.5H6.21973Z" fill="#5A6D80"/>
          </svg>
        </button>
      </div>
    `;

    // Bind highlight toggle
    const toggleCheckbox = toolbar.querySelector(".highlight-toggle-checkbox");
    if (toggleCheckbox) {
      toggleCheckbox.addEventListener("change", function () {
        self.highlightEnabled = this.checked;
        self._applyHighlight();
      });
    }

    // Bind add product button
    const addBtn = toolbar.querySelector(".add-product-btn");
    if (addBtn) {
      addBtn.addEventListener("click", function (e) {
        e.preventDefault();
        if (window.CompareAddProductModal) window.CompareAddProductModal.show();
      });
    }

    // Bind remove all button
    const removeAllBtn = toolbar.querySelector(".remove-all-btn");
    if (removeAllBtn) {
      removeAllBtn.addEventListener("click", function (e) {
        e.preventDefault();
        self._confirmRemoveAll();
      });
    }
  },

  /**
   * Apply highlight to attribute rows
   * @private
   */
  _applyHighlight: function () {
    const self = this;
    document.querySelectorAll(".compare-row-attribute").forEach(function (row) {
      const cells = row.querySelectorAll("td:not(.compare-field-label)");
      const values = Array.from(cells).map(function (c) {
        return c.dataset.value || c.textContent;
      });
      // Filter out empty slot values
      const realValues = values.filter(function (v) {
        return v && v.trim() !== "";
      });
      const allSame = realValues.every(function (v) {
        return v === realValues[0];
      });
      row.classList.toggle(
        "highlight-difference",
        self.highlightEnabled && !allSame
      );
    });
  },

  /**
   * Confirm and remove all products
   * @private
   */
  _confirmRemoveAll: function () {
    if (!window.CompareState) return;

    const products = window.CompareState.getProducts();
    if (products.length === 0) return;

    if (confirm("Remove all products from compare list?")) {
      window.CompareState.clearAll();
    }
  },

  /**
   * Render category badge HTML
   * @private
   * @param {Array} categories - Shared categories
   * @returns {string} Badge HTML
   */
  _renderCategoryBadge: function (categories) {
    if (!categories || categories.length === 0) {
      return '<span class="compare-categories-badge empty">All Categories</span>';
    }

    const names = categories.map(function (c) {
      return c.name;
    });
    return `<span class="compare-categories-badge">
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="14" height="14">
        <path d="M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.87L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z"/>
      </svg>
      ${names.join(" & ")}
    </span>`;
  },

  /**
   * Update category badges across all blocks
   * @private
   * @param {Array} categories - Shared categories
   */
  _updateCategoryBadges: function (categories) {
    const badges = document.querySelectorAll(".compare-categories-badge");
    const newHTML = this._renderCategoryBadge(categories);

    badges.forEach(function (badge) {
      badge.outerHTML = newHTML;
    });
  },

  /**
   * Remove product from compare list
   * @param {string|number} productId - Product ID
   */
  removeProduct: function (productId) {
    if (window.CompareState) {
      window.CompareState.removeProduct(productId);
    }
  },

  /**
   * Add sticky first column functionality
   * @private
   * @param {HTMLElement} table - Table element
   */
  _addStickyFirstColumn: function (table) {
    if (!table) return;

    table.classList.add("sticky-first-column");
    table.querySelectorAll("tr .compare-field-label").forEach(function (cell) {
      cell.classList.add("sticky-cell");
    });

    const container = table.closest(".compare-table-container");
    if (container) {
      container.addEventListener("scroll", function () {
        if (container.scrollLeft > 0) {
          container.classList.add("scrolled");
        } else {
          container.classList.remove("scrolled");
        }
      });
    }
  },

  /**
   * Add touch support for mobile
   * @private
   */
  _addTouchSupport: function () {
    const headers = document.querySelectorAll(".compare-product-header");

    headers.forEach(function (header) {
      header.addEventListener("touchstart", function () {
        headers.forEach(function (h) {
          h.classList.remove("touch-active");
        });
        header.classList.add("touch-active");
      });
    });

    document.addEventListener("touchstart", function (e) {
      headers.forEach(function (header) {
        if (!header.contains(e.target)) {
          header.classList.remove("touch-active");
        }
      });
    });
  },

  /**
   * Initialize popup functionality
   * @private
   */
  _initPopupFunctionality: function () {
    const self = this;
    const triggerButtons = document.querySelectorAll(
      ".compare-table-trigger-button"
    );

    triggerButtons.forEach(function (button) {
      const block = button.closest(".wp-block-brandy-compare-table");
      if (!block) return;

      const wrapper = block.querySelector(".compare-table-wrapper.is-popup");
      if (!wrapper) return;

      button.addEventListener("click", function (e) {
        e.preventDefault();
        self._showPopup(wrapper);
      });
    });
  },

  /**
   * Show popup
   * @private
   * @param {HTMLElement} wrapper - Popup wrapper
   */
  _showPopup: function (wrapper) {
    if (!wrapper) return;

    wrapper.classList.add("is-active");
    this._addCloseButton(wrapper);
    document.body.style.overflow = "hidden";

    const self = this;
    const handleEscape = function (e) {
      if (e.key === "Escape") {
        self._hidePopup(wrapper);
        document.removeEventListener("keydown", handleEscape);
      }
    };
    document.addEventListener("keydown", handleEscape);
  },

  /**
   * Hide popup
   * @private
   * @param {HTMLElement} wrapper - Popup wrapper
   */
  _hidePopup: function (wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove("is-active");
    document.body.style.overflow = "";
  },

  /**
   * Add close button to popup
   * @private
   * @param {HTMLElement} wrapper - Popup wrapper
   */
  _addCloseButton: function (wrapper) {
    const self = this;

    if (!wrapper.querySelector(".popup-close-btn")) {
      const closeBtn = document.createElement("button");
      closeBtn.className = "popup-close-btn";
      closeBtn.innerHTML = "×";
      closeBtn.setAttribute("aria-label", "Close popup");

      closeBtn.addEventListener("click", function () {
        self._hidePopup(wrapper);
      });

      const table = wrapper.querySelector(".brandy-compare-table");
      if (table) table.appendChild(closeBtn);
    }

    wrapper.addEventListener("click", function (e) {
      if (e.target === wrapper) self._hidePopup(wrapper);
    });
  },

  /**
   * Bind add to cart button events
   * @private
   */
  _bindAddToCartEvents: function () {
    document.addEventListener("click", function (e) {
      const btn = e.target.closest(
        ".compare-add-to-cart-btn .add_to_cart_button.ajax_add_to_cart"
      );
      if (!btn) return;

      const container = btn.closest(".compare-add-to-cart-btn");
      if (container) container.classList.add("loading");

      const removeLoading = function () {
        if (container) container.classList.remove("loading");
      };

      document.addEventListener("added_to_cart", removeLoading, { once: true });
      setTimeout(removeLoading, 3000);
    });

    document.addEventListener("added_to_cart", function () {
      document
        .querySelectorAll(".compare-add-to-cart-btn.loading")
        .forEach(function (el) {
          el.classList.remove("loading");
        });
    });
  },
};

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", function () {
  CompareTable.init();
});

// Expose globally
window.CompareTable = CompareTable;

// Legacy global functions for backward compatibility
window.showCompareTablePopup = function (wrapper) {
  CompareTable._showPopup(wrapper);
};
window.handleCompareTableRemoveProduct = function (productId) {
  CompareTable.removeProduct(productId);
};
